// src/oura/merchantV2.ts
//
// One switch for the merchant redesign (#4062 MR-FE-1).
//
// The five client pages are being rearranged around what an owner wants to
// know -- what do I need to do, what are you doing for me, is it working --
// instead of around our own objects. That is eleven items across five pages,
// so it lands in pieces; this is what lets each piece merge without showing a
// half-rearranged product to anyone.
//
// OFF UNLESS EXACTLY "true". Absent is off, like every other Oura flag: a
// missing variable must never turn a redesign on. With it off every client
// page renders as it does today, which is the thing each piece is checked
// against.
//
// Staff screens do not read this. The redesign is client-side only.

export const MERCHANT_V2 = import.meta.env.VITE_ENABLE_OURA_MERCHANT_V2 === "true";

/**
 * The one fixed description under each client page's title (#4062 MR-FE-3).
 *
 * FIXED, not computed. Today four of the five heads carry a line that moves
 * with the data -- counts, a phase tag, "Updated 3 Oct", "late" -- so the
 * sentence that should tell a first-time owner what the page IS changes every
 * time they visit, and the counts repeat what the menu badge and the rows
 * already say. The numbers stay where they are useful; the description stops
 * being one of them.
 */
/** The reports page's name: Documents in the redesign, where it holds every document we send (22 Oct 2026). */
export const REPORTS_PAGE = MERCHANT_V2 ? "Documents" : "Reports";

export const CLIENT_PAGE_SENTENCE = {
    dashboard: "How your month is going.",
    decisions: "Questions from us, for whenever you have a moment.",
    work: "What we are doing for you, and what is done.",
    reports: "Every report and document we have sent you, in one place.",
    access: "The access we need to do the work, and what you have given us.",
} as const;

/**
 * Home's title: "Welcome, Siew Ling", or plain "Welcome".
 *
 * The name comes from the login, so it can be missing, and on a workspace
 * login it is sometimes the business name itself -- "Welcome, Nyonya Table"
 * reads like a form letter, and the business name is already the line above.
 * Either case drops back to "Welcome" rather than greeting someone by the
 * wrong name.
 */
export function welcomeTitle(viewerName: string | null | undefined, businessName: string): string {
    const name = viewerName?.trim();
    if (!name) return "Welcome";
    // Runs of whitespace collapse before the comparison: "Nyonya  Table" and
    // "Nyonya Table" are the same name typed twice, and only one of them
    // matched (Jack, #4063). The name is still DISPLAYED as it was given --
    // normalising is for deciding, not for rewriting someone's name.
    const same = (a: string) => a.trim().replace(/\s+/g, " ").toLowerCase();
    return same(name) === same(businessName) ? "Welcome" : `Welcome, ${name}`;
}

/**
 * A client card's icon tint: its product area today, no tint under the
 * redesign (#4062 MR-FE-4).
 *
 * Eight area tints ran alongside two other colour systems that DO carry
 * meaning -- status, and late -- so "needs you" appeared in three colours and
 * none of them was a rule anyone could learn. On the client pages colour now
 * means status and nothing else; the icons become furniture.
 *
 * Staff pages do not call this. There the area is a real distinction across
 * forty-odd destinations.
 */
export function clientArea<A extends string>(area: A): A | "plain" {
    return MERCHANT_V2 ? "plain" : area;
}
